import {
  IGetEntityMenuResponse,
  IGetUserEntitiesResponse,
  TAppContext,
  TCountry,
  TEntity,
  TLanguage,
  TPrivacyCenterContent,
  TUserType,
} from '../types'
import { isAppContextPrivacyCenterCacheStale } from './app-context-cache'

const APP_CONTEXT_STORAGE_KEY = 'central-privacidade:app-context'

type TPersistedAppContext = Omit<TAppContext, 'dispatch'>

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean'

const isString = (value: unknown): value is string => typeof value === 'string'

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(isString)

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const getStorage = () => {
  if (typeof window === 'undefined') return undefined

  try {
    return window.sessionStorage
  } catch {
    return undefined
  }
}

const parseStoredContext = (value: string) => {
  try {
    return JSON.parse(value) as unknown
  } catch {
    return undefined
  }
}

const getPersistedEntityMenuContent = (value: unknown) => {
  if (!isRecord(value)) return undefined
  if (!isBoolean(value.isActive) || !isRecord(value.content)) return undefined

  return value as IGetEntityMenuResponse
}

const getPersistedPrivacyCenterContent = (value: unknown) => {
  if (!isRecord(value)) return undefined
  if (!isBoolean(value.isActive)) return undefined
  if (!isString(value.entity)) return undefined
  if (!isRecord(value.components) || !isRecord(value.endpoints)) return undefined

  return value as TPrivacyCenterContent
}

const getPersistedEntitiesOrganizations = (value: unknown) => {
  if (!isRecord(value)) return undefined
  if (!isString(value.userIdentifier) || !isString(value.userOrganization)) return undefined
  if (!isRecord(value.accountOrganizations)) return undefined

  return value as IGetUserEntitiesResponse
}

const getPersistedCachedAt = (storedContext: Record<string, unknown>) =>
  isNumber(storedContext.cachedAt) ? storedContext.cachedAt : undefined

const createPersistedContext = (
  storedContext: Record<string, unknown>,
): Partial<TPersistedAppContext> => {
  const cachedAt = getPersistedCachedAt(storedContext)
  const isPrivacyCenterCacheStale = isAppContextPrivacyCenterCacheStale(cachedAt)
  const privacyCenterContent = isPrivacyCenterCacheStale
    ? undefined
    : getPersistedPrivacyCenterContent(storedContext.privacyCenterContent)

  return {
    isAppActive: isBoolean(storedContext.isAppActive) ? storedContext.isAppActive : true,
    isAppLoading: false,
    isAppError: false,
    isQuickLoad: isBoolean(storedContext.isQuickLoad) ? storedContext.isQuickLoad : false,
    isUSBranch: isBoolean(storedContext.isUSBranch) ? storedContext.isUSBranch : false,
    userCountry: isString(storedContext.userCountry)
      ? (storedContext.userCountry as TCountry)
      : undefined,
    userAccountType: isString(storedContext.userAccountType)
      ? (storedContext.userAccountType as TUserType)
      : undefined,
    userEntities: isStringArray(storedContext.userEntities)
      ? (storedContext.userEntities as TEntity[])
      : undefined,
    entitiesOrganizations: getPersistedEntitiesOrganizations(storedContext.entitiesOrganizations),
    userLanguage: isString(storedContext.userLanguage)
      ? (storedContext.userLanguage as TLanguage)
      : undefined,
    userSelectedEntity: isString(storedContext.userSelectedEntity)
      ? (storedContext.userSelectedEntity as TEntity)
      : undefined,
    entityMenuContent: getPersistedEntityMenuContent(storedContext.entityMenuContent),
    privacyCenterContent,
    appContextCachedAt: cachedAt,
  }
}

const getPersistedAppContext = (): Partial<TPersistedAppContext> | undefined => {
  const storage = getStorage()
  const storedContext = (() => {
    try {
      return storage?.getItem(APP_CONTEXT_STORAGE_KEY)
    } catch {
      return undefined
    }
  })()

  if (!storedContext) return undefined

  const parsedContext = parseStoredContext(storedContext)

  if (!isRecord(parsedContext)) return undefined

  return createPersistedContext(parsedContext)
}

const hasAppContextData = (context: Partial<TPersistedAppContext>) =>
  Boolean(
    context.userAccountType &&
      context.userCountry &&
      context.userLanguage &&
      context.userEntities?.length &&
      (context.privacyCenterContent || context.entityMenuContent),
  )

const hasUserContextData = (context: Partial<TPersistedAppContext>) =>
  Boolean(
    context.userAccountType &&
      context.userCountry &&
      context.userLanguage &&
      context.userEntities?.length &&
      (context.userSelectedEntity || context.entityMenuContent),
  )

const persistAppContext = (context: TAppContext) => {
  if (context.isAppError || !hasAppContextData(context)) return

  const storage = getStorage()

  if (!storage) return

  try {
    storage.setItem(
      APP_CONTEXT_STORAGE_KEY,
      JSON.stringify({
        isAppActive: context.isAppActive,
        isAppLoading: false,
        isAppError: false,
        isQuickLoad: context.isQuickLoad,
        isUSBranch: context.isUSBranch,
        userCountry: context.userCountry,
        userAccountType: context.userAccountType,
        userEntities: context.userEntities,
        entitiesOrganizations: context.entitiesOrganizations,
        userLanguage: context.userLanguage,
        userSelectedEntity: context.userSelectedEntity,
        entityMenuContent: context.entityMenuContent,
        privacyCenterContent: context.privacyCenterContent,
        cachedAt: context.appContextCachedAt ?? Date.now(),
      }),
    )
  } catch {
    // Storage can be temporarily unavailable when the WebView process is restored.
  }
}

export {
  getPersistedAppContext,
  hasAppContextData,
  hasUserContextData,
  isAppContextPrivacyCenterCacheStale,
  persistAppContext,
}
export { APP_CONTEXT_CACHE_TTL_MS } from './app-context-cache'
